import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { List, X, CaretDown } from '@phosphor-icons/react';
import { LOGO_URL, NAV_LINKS, COMPANY_INFO } from '@/lib/constants';
import { Button } from '@/components/ui/button';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#0A1F3D]/95 backdrop-blur-xl shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex-shrink-0">
              <img
                src={LOGO_URL}
                alt={COMPANY_INFO.name}
                className="h-12 md:h-16 w-auto object-contain brightness-0 invert"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {NAV_LINKS.map((link) => (
                <div
                  key={link.label}
                  className="relative group"
                  onMouseEnter={() => link.children && setActiveDropdown(link.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={link.href}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`px-4 py-2 text-sm font-medium tracking-wide transition-colors flex items-center gap-1 ${
                      location.pathname === link.href
                        ? 'text-[#D4AF37]'
                        : 'text-white/90 hover:text-[#D4AF37]'
                    }`}
                  >
                    {link.label}
                    {link.children && (
                      <CaretDown
                        size={14}
                        className={`transition-transform ${
                          activeDropdown === link.label ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </Link>

                  {/* Dropdown */}
                  {link.children && (
                    <AnimatePresence>
                      {activeDropdown === link.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 pt-2"
                        >
                          <div className="bg-white shadow-xl rounded-none py-2 min-w-[200px] border-t-2 border-[#D4AF37]">
                            {link.children.map((child) => (
                              <Link
                                key={child.label}
                                to={child.href}
                                data-testid={`nav-dropdown-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                                className="block px-5 py-3 text-sm text-[#4A5568] hover:text-[#0A1F3D] hover:bg-[#F5F5F5] transition-colors"
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </nav>

            {/* Book Now Button */}
            <div className="hidden lg:block">
              <a href="https://www.globaltravelhypermarket.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="book-now-btn"
                  className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] font-semibold px-6 py-2 rounded-none"
                >
                  Book Now
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white"
            >
              {isMobileMenuOpen ? <X size={28} /> : <List size={28} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="absolute right-0 top-0 h-full w-[300px] bg-[#0A1F3D] overflow-y-auto">
              <div className="pt-24 pb-8 px-6">
                <nav className="space-y-1">
                  {NAV_LINKS.map((link) => (
                    <div key={link.label}>
                      <Link
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                        className="block py-3 text-white/90 hover:text-[#D4AF37] font-medium border-b border-white/10"
                      >
                        {link.label}
                      </Link>
                      {link.children && (
                        <div className="pl-4 pb-2">
                          {link.children.map((child) => (
                            <Link
                              key={child.label}
                              to={child.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="block py-2 text-sm text-white/70 hover:text-[#D4AF37]"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
                <div className="mt-8">
                  <a href="https://www.globaltravelhypermarket.com" target="_blank" rel="noopener noreferrer">
                    <Button
                      data-testid="mobile-book-now-btn"
                      className="w-full bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] font-semibold py-3 rounded-none"
                    >
                      Book Now
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
